import React, { useCallback, useContext, useEffect, useState } from 'react';
import { fileListToArray } from '../../lib/js/Array';
import FileInput from '../../components/molecules/Form/models/InputModels/FileInput';
import { useFormContext } from '../../components/molecules/Form';
import { InteractionStatus } from '../../types/InteractionStatus';
import { InputProps } from '../../types/HtmlProps';
import { setCustomValidity, ValidationRule } from './utils/validate';
import { getAcceptString } from './utils/getAcceptString';
import { useValdidationRules } from './utils/useValdidationRules';
import { getConstraints } from './utils/constraints';
import ModalContext from '../../components/organisms/ModalDialog/ModalContext';
import { KeyEvent } from '../../types/KeyEvent';

export interface SelectedFile {
  file: File;
  errors: string[];
}

type FileUploadProps<T> = {
  input: FileInput;
  id: string;
  clearValue?: boolean;
  fileName?: string;
  onChange(selection: T[]): void;
};

export type UseFileUpload<T = SelectedFile> = FileUploadProps<T>;

export function useBasicFileUpload<T extends SelectedFile = SelectedFile>({
  input,
  id,
  clearValue,
  onChange,
  validate,
}: FileUploadProps<T> & {
  validate(files: File, rules: ValidationRule[]): Promise<T>;
}) {
  const validationRules = useValdidationRules(input.constraints || {});
  const inputRef = React.useRef<HTMLInputElement>(null);
  const labelRef = React.useRef<HTMLLabelElement>(null);
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  const [selection, setSelection] = useState<T[]>([]);

  const removeFile = (itemToRemove: File | ((item: SelectedFile) => boolean)) => {
    // careful her, standard filter compares object references of files. if provided file object was copied or
    // created new and therefore object reference differs from the file object stored in selection
    // filter will not work!!!
    const filter = typeof itemToRemove === 'function' ? itemToRemove : (item: T) => item.file !== itemToRemove;
    const remaining = selection.filter(filter);
    onChange(remaining);
    setSelection(remaining);
  };

  const reset = useCallback(() => {
    onChange([]);
    setSelection([]);
  }, [onChange, setSelection]);

  const { model, status } = useFormContext();
  const modalContext = useContext(ModalContext);

  /**
   * Here we need to clear the value after successfully saving, because at the image gallery
   * and file attachments the form remains open. If the form is in modal, then we don't need to clear it.
   * Otherwise, image disappears before modal is closed.
   * */
  useEffect(() => {
    if (modalContext !== null) {
      return;
    }
    if (status === InteractionStatus.SUCCESS) {
      const handle = setTimeout(() => {
        reset();
      }, 2000);
      return () => clearTimeout(handle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onSelectedFilesChanged = async (files: File[]) => {
    setIsReadingFiles(true);
    // reset status of model after selection changed to clear pending errors:
    if (model.status === InteractionStatus.FAIL) {
      model.status = InteractionStatus.NONE;
    }

    const validatedFiles = await Promise.all(files.map(file => validate(file, validationRules)));
    labelRef.current?.focus();
    setSelection(validatedFiles);
    onChange(validatedFiles);

    setIsReadingFiles(false);
  };

  input.registerInputRef(inputRef);

  /** Handles various ways to setCustomValidity on the input element
   *
   * @param errors can be an array of error messages or simply a string
   * */
  const setValidity = (errors: string[] | string) => {
    const errorString = Array.isArray(errors)
      // Set() only allowing unique values makes sure we don't join duplicate error strings
      ? [...new Set(errors)].join('\n')
      : errors;
    setCustomValidity(inputRef, errorString);
  };

  /**
   *  Accessibility: the upload function must be available with enter key on faked button
   * @param event keydownEvent
   */
  const handleOnKeyDown = (event: React.KeyboardEvent<any>) => {
    if ((event.code === KeyEvent.Enter || event.code === KeyEvent.Space) && inputRef.current) {
      event.preventDefault();
      if (inputRef.current) {
        inputRef.current.click();
      }
    }
  };

  /**
   * Here value of target is set to empty string so that the same file can be uploaded a second time.
   * See: SIBZIEL-12612, SIBZIEL-13166
   * should we always clear the input here ?
   */
  const handleOnChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await onSelectedFilesChanged(fileListToArray(event.target.files));
    if (clearValue) {
      event.target.value = '';
    }
  };

  const inputProps = {
    name: input.name,
    id: `file-upload-${id}`,
    accept: getAcceptString(input.constraints),
    multiple: input.multiple,
    disabled: input.multiple && status === InteractionStatus.SENDING,
    ref: inputRef,
    onChange: handleOnChange,
    ...getConstraints(input),
  } as InputProps;

  const labelProps = {
    ref: labelRef,
    onKeyDown: handleOnKeyDown,
  };

  return {
    inputProps,
    labelProps,
    setValidity,
    isReadingFiles,
    selection,
    removeFile,
    reset,
  };
}
